import * as React from 'react';
import { Box, ClickAwayListener, Stack, Typography } from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import { SxProps, Theme } from '@mui/material/styles';
import { useHits, useSearchBox } from 'react-instantsearch';
import useApplyClientHit from '../../hooks/customers/useApplyClientHit';
import { getClientDisplay } from '../../helpers/customers';

interface Props {
  namePrefix: string[];
  onCreateNewClient: (query: string) => void;
  onReset: () => void;
  onSelectClient: () => void;
}

const rowSx: SxProps<Theme> = {
  cursor: 'pointer',
  borderBottom: 1,
  borderColor: theme => theme.palette.darkerLightGrey.main,
  '&:hover': { background: theme => theme.palette.lightGrey.main },
};

const avatarSx: SxProps<Theme> = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  flexShrink: 0,
  background: theme => theme.palette.lightGrey.main,
  color: 'text.secondary',
  typography: 'caption',
  fontWeight: 600,
};

// Renders its own markup off useHits/useSearchBox rather than <Hits>, so none
// of the .ais-* classes the satellite theme styles are emitted here.
export default function ClientSearchResults({
  namePrefix,
  onCreateNewClient,
  onReset,
  onSelectClient,
}: Props) {
  const { query, refine, clear } = useSearchBox();
  const { items } = useHits();
  const applyHit = useApplyClientHit(namePrefix);

  // Classic opens the list on focus, before anything is typed — preserved here.
  const [showHits, setShowHits] = React.useState(false);

  const trimmedQuery = query.trim();
  // Committing to a new client closes the list once, in handleCreate. Typing
  // here again is a search, so it has to be able to open it back up.
  const showResults = showHits;

  const handleReset = () => {
    clear();
    refine('');
    setShowHits(false);
    onReset();
  };

  const handleCreate = () => {
    setShowHits(false);
    onCreateNewClient(trimmedQuery);
  };

  const handlePick = (hit: any) => {
    setShowHits(false);
    applyHit(hit);
    // Picking an existing client abandons a new one started before it, so the
    // card takes over from the new-client fields.
    onSelectClient();
  };

  return (
    <ClickAwayListener onClickAway={() => setShowHits(false)}>
      <Stack gap={1} position="relative">
        <Stack
          direction="row"
          alignItems="center"
          gap={1.25}
          sx={{
            borderRadius: '5px',
            padding: '13px 16px',
            background: theme => theme.palette.common.white,
            border: 1,
            borderColor: theme =>
              trimmedQuery
                ? theme.palette.error.main
                : theme.palette.lightOutlineGrey.main,
            // The inner input clears its own outline, so focus shows here.
            '&:focus-within': {
              borderColor: theme => theme.palette.darkGrey.main,
            },
          }}>
          <SearchRounded
            sx={{
              fontSize: 18,
              color: theme => theme.palette.outlineGrey.main,
            }}
          />
          <Box
            component="input"
            type="text"
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              refine(e.target.value);
              setShowHits(true);
            }}
            placeholder="Select or create client"
            autoComplete="off"
            onFocus={() => setShowHits(true)}
            sx={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'Montserrat, sans-serif',
              color: theme => theme.palette.common.black,
              typography: 'subtitle2',
            }}
          />
          {(!!trimmedQuery || showResults) && (
            <Stack
              role="button"
              onClick={handleReset}
              sx={{
                cursor: 'pointer',
                color: theme => theme.palette.outlineGrey.main,
                '&:hover': { color: theme => theme.palette.common.black },
              }}>
              <CloseRounded sx={{ fontSize: 16 }} />
            </Stack>
          )}
        </Stack>

        {showResults && (
          <Stack
            sx={{
              position: 'absolute',
              // The search box is the only in-flow child, so 100% clears it
              // whatever it measures.
              top: '100%',
              marginTop: '6px',
              left: 0,
              right: 0,
              zIndex: 20,
              borderRadius: '10px',
              maxHeight: 340,
              overflowY: 'auto',
              background: theme => theme.palette.common.white,
              border: 1,
              borderColor: theme => theme.palette.darkerLightGrey.main,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            }}>
            <Stack
              direction="row"
              alignItems="center"
              gap={1.25}
              onClick={handleCreate}
              sx={{ ...rowSx, padding: '14px 16px' }}>
              <AddCircleOutlineRounded
                sx={{
                  fontSize: 17,
                  color: theme => theme.palette.primary.main,
                }}
              />
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                {trimmedQuery ? (
                  <>
                    Create new client &ldquo;
                    <Box component="span" fontWeight={700}>
                      {trimmedQuery}
                    </Box>
                    &rdquo;
                  </>
                ) : (
                  'Create new client'
                )}
              </Typography>
            </Stack>

            {items.map((hit: any) => {
              const { initials, name, meta } = getClientDisplay(
                hit,
                null,
                ', ',
              );

              return (
                <Stack
                  key={hit.objectID}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  onClick={() => handlePick(hit)}
                  sx={{ ...rowSx, padding: '12px 16px' }}>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={avatarSx}>
                    {initials}
                  </Stack>
                  <Stack gap={0.375} minWidth={0}>
                    <Typography variant="h5" lineHeight={1}>
                      {name}
                    </Typography>
                    {!!meta && (
                      <Typography
                        variant="caption"
                        lineHeight={1}
                        sx={{ color: 'text.tertiary' }}>
                        {meta}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              );
            })}

            {!items.length && (
              <Typography
                variant="subtitle2"
                sx={{
                  padding: '14px 16px',
                  color: 'text.tertiary',
                }}>
                No matching clients
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </ClickAwayListener>
  );
}
